import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Drawer from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { X } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import {
  isUnread,
  listNotifications,
  markNotificationRead,
  notificationDestination,
  type InboxItem,
} from "@/lib/brokerNotifications";
import { messageFor } from "@/lib/errors";
import { formatNoticeTime } from "@/lib/listFormat";
import { NoticeKindLabel } from "@/components/InboxRowParts";

import { buttonClass } from "@/components/buttonStyles";
// The bell's panel (BRK-S252): the latest notifications, sliding in from the
// right. Each opening reads them afresh, and PMS marks the rows it returns SEEN,
// so `onLoaded` tells the bell to re-read its badge. A row that names a record
// opens it, and is marked read on the way (best-effort); "See all
// notifications" goes to the inbox, the BaseListView page. MUI's Drawer brings
// the dialog's focus trap, Escape, the backdrop, and focus's return to the bell.

/** How many the panel shows; the inbox has the rest. */
export const PANEL_LIMIT = 10;

type PanelState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; items: InboxItem[] };

export function NotificationsPanel({
  open,
  onClose,
  onLoaded,
}: {
  open: boolean;
  onClose: () => void;
  /** The rows were read, so PMS has marked them seen. */
  onLoaded: () => void;
}) {
  const { authedRequest } = useAuth();
  const titleId = useId();
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)", {
    noSsr: true,
  });
  const [state, setState] = useState<PanelState>({ kind: "loading" });
  const [attempt, setAttempt] = useState(0);
  const loaded = useRef(onLoaded);
  loaded.current = onLoaded;

  useEffect(() => {
    if (!open) return;
    let active = true;
    setState({ kind: "loading" });
    listNotifications(authedRequest, { limit: PANEL_LIMIT, offset: 0 })
      .then((page) => {
        if (!active) return;
        setState({ kind: "ready", items: page.items });
        loaded.current();
      })
      .catch((err: unknown) => {
        if (active) setState({ kind: "error", message: messageFor(err) });
      });
    return () => {
      active = false;
    };
  }, [open, attempt, authedRequest]);

  function follow(item: InboxItem) {
    // The broker is on their way; a failed mark only leaves the row unread.
    markNotificationRead(authedRequest, item.id).catch(() => {});
    onClose();
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      transitionDuration={reduceMotion ? 0 : undefined}
      slotProps={{
        paper: {
          role: "dialog",
          "aria-modal": true,
          "aria-labelledby": titleId,
          sx: {
            width: "100%",
            "@media (min-width: 640px)": { width: 400 },
          },
        },
      }}
    >
      <div className="flex h-full flex-col text-brk-text">
        <header className="flex items-center justify-between gap-3 border-b border-brk-border px-4 py-3">
          <h2 id={titleId} className="text-heading">
            Notifications
          </h2>
          <button
            type="button"
            aria-label="Close notifications"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-brk-dim transition-colors hover:bg-brk-bg hover:text-brk-text"
          >
            <X size={18} aria-hidden />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {state.kind === "loading" && <PanelSkeleton />}
          {state.kind === "error" && (
            <div
              role="alert"
              className="m-4 flex flex-col items-start gap-3 rounded-md border border-brk-border p-4 text-sm"
            >
              <p className="text-brk-danger">{state.message}</p>
              <button
                type="button"
                onClick={() => setAttempt((n) => n + 1)}
                className={buttonClass({ variant: "secondary", size: "sm" })}
              >
                Retry
              </button>
            </div>
          )}
          {state.kind === "ready" && state.items.length === 0 && (
            <div className="px-6 py-12 text-center">
              <p className="text-sm font-semibold">You’re all caught up</p>
              <p className="mt-1 text-sm text-brk-dim">
                New notifications about your submissions, bonds, and account
                appear here.
              </p>
            </div>
          )}
          {state.kind === "ready" && state.items.length > 0 && (
            <ul className="divide-y divide-brk-border">
              {state.items.map((item) => (
                <PanelRow key={item.id} item={item} onFollow={follow} />
              ))}
            </ul>
          )}
        </div>

        <footer className="border-t border-brk-border px-4 py-3 text-right">
          <Link
            to="/notifications"
            onClick={onClose}
            className="text-sm font-medium text-brk-accent hover:underline"
          >
            See all notifications
          </Link>
        </footer>
      </div>
    </Drawer>
  );
}

/** One notice: a link when it names a record, text otherwise. */
function PanelRow({
  item,
  onFollow,
}: {
  item: InboxItem;
  onFollow: (item: InboxItem) => void;
}) {
  const unread = isUnread(item);
  const destination = notificationDestination(item);
  const content = (
    <>
      <span
        aria-hidden="true"
        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
          unread ? "bg-brk-accent" : ""
        }`}
      />
      <span className="min-w-0 flex-1">
        {/* The notice's kind above its subject, as on the inbox (BRK-S188). */}
        <NoticeKindLabel item={item} />
        <span
          className={`mt-0.5 block text-sm ${unread ? "font-semibold" : "font-normal"}`}
        >
          {item.subject}
        </span>
        {unread && <span className="sr-only">Unread</span>}
        <span className="mt-0.5 block text-xs text-brk-dim">
          {formatNoticeTime(item.createdAt)}
        </span>
        {destination && (
          <span className="mt-1 block text-xs font-medium text-brk-accent">
            {destination.label}
          </span>
        )}
      </span>
    </>
  );
  return (
    <li>
      {destination ? (
        <Link
          to={destination.to}
          onClick={() => onFollow(item)}
          className="flex gap-3 px-4 py-3 transition-colors hover:bg-brk-bg"
        >
          {content}
        </Link>
      ) : (
        <div className="flex gap-3 px-4 py-3">{content}</div>
      )}
    </li>
  );
}

function PanelSkeleton() {
  return (
    <div aria-busy="true">
      <p role="status" className="sr-only">
        Loading notifications…
      </p>
      <ul aria-hidden="true" className="divide-y divide-brk-border">
        {[0, 1, 2].map((i) => (
          <li key={i} className="flex gap-3 px-4 py-3">
            <span className="mt-1.5 h-2 w-2 shrink-0" />
            <span className="flex-1 space-y-2">
              <span className="block h-3.5 w-3/4 animate-pulse rounded bg-brk-bg" />
              <span className="block h-3 w-1/3 animate-pulse rounded bg-brk-bg" />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
